import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_aplicacion_recipientes";
const DB_VERSION = 2;
const STORE_RECIPIENTES = "recipientes";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcTareaItem`: watermark por cliente, no uno global). */
export type GrpcAplicacionRecipienteItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB de los recipientes (canecas, tanques) que trae
 *  `AplicacionRecipienteGrpcService` — mismo patrón que `GrpcTareaDatabase`,
 *  que es el otro listado con **globales + propios del cliente**: la unión la
 *  resuelve el server, así que acá no hace falta índice por cliente y el store
 *  guarda las dos clases juntas.
 *
 *  **Versión 2 (2026-10-15)**: los dos recipientes estándar dejaron de ser
 *  globales y pasaron a sembrarse por cliente, lo que borró las filas globales
 *  del servidor **sin baja lógica** —las borró una migración—, así que ningún
 *  delta las quita del navegador y quedarían ahí para siempre. Se ven filtradas
 *  (`clienteId` no coincide) pero son basura; vaciar es más limpio que
 *  arrastrarlas. */
class GrpcAplicacionRecipienteDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de versión
   *  conserva los datos (ver `fx_crearBaseLocal`). */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_RECIPIENTES, clave: "id" },
      { nombre: STORE_SYNC, clave: "clienteId" },
    ],
    { vaciarAntesDe: 2 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(clienteId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<
      { clienteId: string; ultimaSincronizacion: string } | undefined
    >(store.get(clienteId));
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcAplicacionRecipienteItem>(
    clienteId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_RECIPIENTES, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_RECIPIENTES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({
      clienteId,
      ultimaSincronizacion: nuevoWatermark.toString(),
    });

    await txDone(tx);
  }

  /** Todos los recipientes guardados (globales + del cliente logueado: el
   *  server ya resuelve esa unión, acá no hace falta filtrar por índice). */
  async getListAsync<T>(): Promise<T[]> {
    const db = await this.open();
    const store = db
      .transaction(STORE_RECIPIENTES, "readonly")
      .objectStore(STORE_RECIPIENTES);
    return reqToPromise<T[]>(store.getAll());
  }

  /** Actualiza (o inserta) un solo recipiente sin tocar el resto. */
  async putOneAsync<T extends GrpcAplicacionRecipienteItem>(
    item: T,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_RECIPIENTES, "readwrite");
    tx.objectStore(STORE_RECIPIENTES).put(item);
    await txDone(tx);
  }

  /** Borra un recipiente puntual (baja confirmada por el server). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_RECIPIENTES, "readwrite");
    tx.objectStore(STORE_RECIPIENTES).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de recipientes de aplicación (con delta por cliente). */
export const grpcAplicacionRecipienteDatabase =
  new GrpcAplicacionRecipienteDatabase();
